/* eslint-disable react/require-default-props */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Skeleton } from '@one-thd/sui-atomic-components';
import { extend } from '@thd-nucleus/data-sources';
import useDeviceType from '../../hooks/useDeviceType';
import { CustomSkeletonLine } from './CustomSkeletonLine';
import { EmptyContainer } from './EmptyContainer';
import { RemoveDrawer } from './RemoveDrawer';
import { SwapProductLoader } from './SwapProductLoader';
import { tiggerAnalyticsCustomEvent, isProductActive, isAPOFPOZipCode } from '../../utils/product-bundle-utils';
import { SwapOptions } from './SwapOptions';
import { ProductListItem } from './ProductListItem';
import { showEmptyContainer, getMissingProductsCount } from '../../utils/product-unavailable-utils';
import { ObsoleteEmptyContainer } from './productUnavailable/ObsoleteEmptyContainer';
import '../../styles/productListContent.scss';
import { ProductAlertWrapper } from './ProductAlertWrapper';
import { PRICE_TYPES } from '../../constants/codes';

/**
 * Props
 *
 * @typedef {object} Props
 * @property {{bundleSpecificationDetails: {type: string}}} product Bundle product
 * @property {array} products - The number of products in the bundle
 * @property {string} channel - If it is Desktop or Mobile
 * @typedef {object} accordionProps
 *  * @property {string} productSpecificationAccordionName Specification Accordion name to open and
 * close from parent accordion group
 *  * @property {number} scrollStickyHeaderHeight Height used to offset the scrolling action
 *  * @property {string} productDetailsAccordionName Product Details Accordion name to open and
 * close from parent accordion group
 */

/**
 * Returns a formatted Product List of the provided products.
 * @param {Props} Props
 * @returns {React.ReactElement} JSX
 */

const ProductListContent = ({
  products,
  product: bundleProduct,
  addProduct,
  removeProduct,
  swapProducts,
  swapBack,
  loading,
  accordionProps,
  selectableVariantProducts,
  variantChangeAlertMessage,
  bundleProductsCount = 0,
  dismissAlert
}) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [hasApoFpoZipCode, setHasApoFpoZipCode] = useState(false);

  const { isMobile } = useDeviceType();

  useEffect(() => {
    setHasApoFpoZipCode(isAPOFPOZipCode());
  }, []);

  const removeDrawerProduct = products?.find((product) => product.itemId === selectedItemId);
  const triggerAnalytics = (index) => {
    const analyticsData = {
      position: `${index + 1}`
    };
    tiggerAnalyticsCustomEvent('remove-item.success', analyticsData);
  };
  const missingProductsCount = getMissingProductsCount(bundleProductsCount, products);

  const skeletonRowCount = bundleProductsCount || bundleProduct?.productComponent?.length || 3;

  return (
    <ul data-testid="product-list-content" className="sui-w-full">
      {products?.length > 0 ? (
        <>
          {products.map((product, index) => {
            const selectableVariants = bundleProduct?.productComponent?.[product?.priority - 1]?.selectableVariants;

            const isProductRemoved = showEmptyContainer(bundleProduct, product);

            return (
              <React.Fragment key={product?.itemId || index}>
                <li
                  key={product?.itemId}
                  className="product-list-item sui-flex sui-flex-col sui-gap-4"
                  data-testid="product-list-item"
                >
                  {isProductRemoved ? (
                    <>
                      <ProductAlertWrapper
                        bundle={bundleProduct}
                        product={product}
                        dismissAlert={dismissAlert}
                        variantText={variantChangeAlertMessage}
                        onSwapBack={swapBack}
                      />
                      <EmptyContainer
                        product={product}
                        loading={loading && selectedItemId === product.itemId}
                        onClick={addProduct}
                        setSelectedItemId={setSelectedItemId}
                        isMobile={isMobile}
                        bundleProduct={bundleProduct}
                      />
                    </>
                  ) : (
                    <React.Fragment key={`product-content-${product.itemId || index}`}>
                      <ProductAlertWrapper
                        bundle={bundleProduct}
                        product={product}
                        dismissAlert={dismissAlert}
                        variantText={variantChangeAlertMessage}
                        onSwapBack={swapBack}
                        hasApoFpoZipCode={hasApoFpoZipCode}
                      />
                      <div className="sui-w-full">
                        {loading && selectedItemId === product.itemId ? (
                          <SwapProductLoader key={`removed-${product.itemId}`} />
                        ) : (
                          <>
                            <ProductListItem
                              product={product}
                              accordionProps={accordionProps}
                              selectableVariants={selectableVariants}
                              bundleProduct={bundleProduct}
                              swapProducts={swapProducts}
                              selectableVariantProducts={selectableVariantProducts}
                              priceType={product?.quantity > 1 ? PRICE_TYPES.MINIMAL : PRICE_TYPES.SIMPLE}
                              actionComponent={(
                                <SwapOptions
                                  product={product}
                                  setSelectedItemId={setSelectedItemId}
                                  swapProducts={swapProducts}
                                  index={index}
                                  triggerAnalytics={triggerAnalytics}
                                  setIsDrawerOpen={setIsDrawerOpen}
                                  isDrawerOpen={isDrawerOpen}
                                  isMobile={isMobile}
                                />
                              )}
                            />
                          </>
                        )}
                      </div>
                    </React.Fragment>
                  )}
                </li>
                <hr
                  key={`separater-${product?.itemId || index}`}
                  className="sui-my-4 sui-w-full sui-h-0 sui-border-primary last-of-type:sui-hidden"
                />
              </React.Fragment>
            );
          })}
        </>
      ) : (
        [...new Array(skeletonRowCount).keys()].map((item) => (
          <li
            key={item}
            // eslint-disable-next-line max-len
            className="product-list-item first:sui-pt-0 last:sui-pb-0 last:sui-border-0 sui-flex sui-items-start sui-border-b-1 sui-border-primary sui-border-solid sui-py-4"
            data-testid="product-list-item"
          >
            <div className="sui-w-full" data-testid="skeleton-loader">
              <Skeleton grow disableGutters disablePadding>
                <CustomSkeletonLine height="96px" />
              </Skeleton>
            </div>
          </li>
        ))
      )}
      <ObsoleteEmptyContainer bundleProduct={bundleProduct} missingProductsCount={missingProductsCount} />
      {
        isDrawerOpen && (
          <RemoveDrawer
            closeDrawer={() => setIsDrawerOpen(false)}
            itemId={selectedItemId}
            setSelectedItemId={setSelectedItemId}
            isDrawerOpen={isDrawerOpen}
            storeId="121"
            products={products}
            removeProduct={removeProduct}
            swapProducts={swapProducts}
            loading={loading}
            isMobile={isMobile}
            hideCurrentSelectedItem={removeDrawerProduct
              ? !isProductActive(removeDrawerProduct) : false}
          />
        )
      }
    </ul>
  );
};

ProductListContent.propTypes = {
  products: PropTypes.arrayOf(
    PropTypes.shape({
      itemId: PropTypes.string,
      dataSource: PropTypes.string
    })
  ),
  product: PropTypes.shape({
    bundleSpecificationDetails: PropTypes.shape({
      type: PropTypes.string
    }),
    features: PropTypes.shape({
      showProductUpdateAlert: PropTypes.bool,
      showProductUpdateGeneralAlert: PropTypes.bool,
      templates: PropTypes.shape({
        [PropTypes.string]: PropTypes.string
      })
    }),
    productComponent: PropTypes.arrayOf(
      PropTypes.shape({
        selectableVariants: PropTypes.shape({
          [PropTypes.string]: PropTypes.arrayOf(
            PropTypes.shape({
              id: PropTypes.string.isRequired,
              value: PropTypes.string.isRequired
            })
          )
        })
      })
    )
  }),
  accordionProps: PropTypes.shape({
    productSpecificationAccordionName: PropTypes.string,
    scrollStickyHeaderHeight: PropTypes.number,
    productDetailsAccordionName: PropTypes.string
  }).isRequired,
  addProduct: PropTypes.func.isRequired,
  removeProduct: PropTypes.func.isRequired,
  swapProducts: PropTypes.func.isRequired,
  swapBack: PropTypes.func.isRequired,
  dismissAlert: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  selectableVariantProducts: PropTypes.arrayOf(PropTypes.shape({ })).isRequired,
  variantChangeAlertMessage: PropTypes.string.isRequired,
  bundleProductsCount: PropTypes.number
};

ProductListContent.displayName = 'ProductListContent';

ProductListContent.dataModel = extend(ProductListItem);

export { ProductListContent };
